import type * as Highcharts from 'highcharts';
import { merge } from 'lodash';
import { Encoding, MarkType } from '@mode-vegas/vegas';

import { ChartRenderOptions } from '@mode/charts/domain';
import { lookupYField, RenderDataModel } from '@mode/charts/util-vegas';
import { ChartData } from './chart-data';
import { ChartEvent } from './chart-event';
import { ChartFormatter } from './chart-format';
import { ChartTitle, ChartTitles } from './chart-title';
import { ChartTypes, FlamingoTypes, ChartRenderTypes } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT } from '@mode/shared/util-js';

export async function importRenderFunc() {
  const highcharts = await import('highcharts/highstock');
  // Load additional modules and initialize
  await Promise.all([import('highcharts/modules/annotations'), import('highcharts/highcharts-more')]).then((modules) =>
    modules.forEach((module) => module.default(highcharts))
  );
  return highcharts;
}

export namespace ChartRenderer {
  export async function renderGroupedBarChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    horizontal: boolean,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeLineData(
      [ChartData.transformNullstoZero],
      chartFormat,
      initGroupedData(model, encoding, chartFormat)
    );

    const chartInitFunc = horizontal ? initBarChart : initColumnChart;

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatIndividualTooltipContent,
        ChartFormatter.formatPlotSeriesDataLabels,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      chartInitFunc(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );
    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderStackedBarChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    horizontal: boolean,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeLineData(
      [ChartData.transformNullstoZero],
      chartFormat,
      initStackedData(model, encoding, chartFormat)
    );

    const chartInitFunc = horizontal ? initStackedBarChart : initStackedColumnChart;

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatStackedBarXAxisPadding,
        ChartFormatter.formatStackedYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatSharedTooltips,
        ChartFormatter.formatTotalAreaTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      chartInitFunc(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderStackedBar100Chart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    horizontal: boolean,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeLineData(
      [ChartData.transformNullstoZero],
      chartFormat,
      initStackedData(model, encoding, chartFormat)
    );

    const chartInitFunc = horizontal ? initStackedBar100Chart : initStackedColumn100Chart;

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatStackedBarXAxisPadding,
        ChartFormatter.formatStacked100YAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatSharedTooltips,
        ChartFormatter.formatTotalAreaTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      chartInitFunc(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderComboChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeLineData(
      [ChartData.transformNullstoZero],
      chartFormat,
      initComboData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatComboYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatSharedTooltips,
        ChartFormatter.formatSharedTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initComboChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderLineChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeLineData(
      [ChartData.transformNullstoZero, ChartData.transformOrphanPoints(encoding.x[0].variable.type)],
      chartFormat,
      initStackedData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatSharedTooltips,
        ChartFormatter.formatSharedTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initLineChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderAreaChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeLineData(
      [ChartData.transformNullstoZero],
      chartFormat,
      initStackedData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatSharedTooltips,
        ChartFormatter.formatAreaTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initAreaChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderTotalAreaChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeLineData(
      [ChartData.transformNullstoZero],
      chartFormat,
      initStackedData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatTotalAreaYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatSharedTooltips,
        ChartFormatter.formatTotalAreaTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initTotalAreaChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderScatterChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    // let chartData = makePointData([
    // ], chartFormat, initScatterData(model, encoding, chartFormat));
    const chartData = makeLineData(
      [ChartData.transformNullstoZero],
      chartFormat,
      initStackedData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatScatterXAxisRange,
        ChartFormatter.formatYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatIndividualTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initScatterChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );
    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderBubbleChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makeBubbleData(
      [
        // Bubble Data Transforms
      ],
      chartFormat,
      initBubbleData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatNullsOutOfContinuousCharts,
        ChartFormatter.formatXAxis,
        ChartFormatter.formatYAxis,
        ChartFormatter.formatLegend,
        ChartFormatter.formatIndividualTooltipContent,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initBubbleChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderPieChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makePointData(
      [
        // Pie Data Transforms
      ],
      chartFormat,
      initPieData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatPieLegend,
        ChartFormatter.formatPieTooltipContent,
        ChartFormatter.formatPlotSeriesDataLabels,
        ChartFormatter.formatPieDataLabels,
        ChartFormatter.formatPieSize,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initPieChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  export async function renderDonutChart(
    model: RenderDataModel,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    element: HTMLElement,
    renderOptions?: ChartRenderOptions
  ): Promise<Highcharts.Chart> {
    const chartFormat = format || DEFAULT_CHART_FORMAT;

    const chartData = makePointData(
      [ChartData.transformPointInnerSize],
      chartFormat,
      initDonutData(model, encoding, chartFormat)
    );

    const chartOptions = makeChartOptions(
      [
        ChartFormatter.formatPieLegend,
        ChartFormatter.formatPieTooltipContent,
        ChartFormatter.formatPlotSeriesDataLabels,
        ChartFormatter.formatPieDataLabels,
        ChartFormatter.formatPieSize,
      ],
      model,
      encoding,
      chartData,
      chartFormat,
      initPieChart(model, encoding, chartData, chartFormat, renderOptions),
      renderOptions
    );

    const chart = (await importRenderFunc()).chart;
    return chart(element, chartOptions);
  }

  /**
   * Chart Builders
   */

  function makeLineData(
    dataTransforms: ChartRenderTypes.LineDataTransform[],
    chartFormat: ChartTypes.ChartFormat,
    initChartData: ChartRenderTypes.LineData
  ): ChartRenderTypes.LineData {
    return [...(dataTransforms || [])].reduce(
      (chartData, transform) => transform(chartData, chartFormat),
      initChartData
    );
  }

  function makePointData(
    dataTransforms: ChartRenderTypes.PointDataTransform[],
    chartFormat: ChartTypes.ChartFormat,
    initChartData: ChartRenderTypes.PointData
  ): ChartRenderTypes.PointData {
    return [...(dataTransforms || [])].reduce(
      (chartData, transform) => transform(chartData, chartFormat),
      initChartData
    );
  }

  function makeBubbleData(
    dataTransforms: ChartRenderTypes.BubbleDataTransform[],
    chartFormat: ChartTypes.ChartFormat,
    initChartData: ChartRenderTypes.BubbleData
  ): ChartRenderTypes.BubbleData {
    return [...(dataTransforms || [])].reduce(
      (chartData, transform) => transform(chartData, chartFormat),
      initChartData
    );
  }

  function makeChartOptions(
    chartFormatters: ChartFormatter[],
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.ChartData,
    chartFormat: ChartTypes.ChartFormat,
    initChartOptions: Partial<Highcharts.Options>,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    return [
      ChartFormatter.formatChart,
      ChartFormatter.formatColors,
      ChartFormatter.formatPlotSeries,
      ChartFormatter.formatSeries,
      ChartFormatter.formatTitle,
      ...(chartFormatters || []),
    ].reduce(
      (chartOptions, formatter) => formatter(model, encoding, chartOptions, chartData, chartFormat, renderOptions),
      initChartOptions
    );
  }

  /**
   * Chart Initializers
   */

  function initComboChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeComboMarkEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          line: {
            cursor: cursorState,
            events: markEvents,
            marker: {
              enabled: true,
              fillColor: null,
              lineColor: null,
              radius: 0.1, // imitates hidden nodes while allowing proper selected state behavior
              symbol: 'circle',
              states: {
                hover: {
                  lineWidth: 0.5,
                  lineColor: 'black',
                  radius: 6,
                },
                select: {
                  fillColor: null,
                  lineWidth: 0.5,
                  radius: 6,
                },
              },
            },
          },
          column: {
            cursor: cursorState,
            events: markEvents,
            states: {
              hover: {
                borderColor: 'black',
                brightness: 0, // disable color change on-hover
              },
              select: {
                color: null,
              },
            },
          },
          series: {
            allowPointSelect: selectionState,
            stickyTracking: false, // prevents tooltip from appearing far away from line segment
          },
        },
      },
      initComboTitles(chartFormat, ChartTitle.makeComboTitles(model, encoding))
    );
  }

  // horizontal grouped bar
  function initBarChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeGroupedBarMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'bar',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          bar: {
            allowPointSelect: selectionState,
            cursor: cursorState,
            events: markEvents,
            dataLabels: {
              inside: false,
            },
            states: {
              select: {
                color: null,
              },
              hover: {
                brightness: 0, // disable color change on-hover
                halo: {
                  attributes: {
                    stroke: 'black',
                    'stroke-width': 1,
                  },
                },
              },
            },
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeGroupedTitles(model, encoding))
    );
  }

  function initColumnChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeGroupedBarMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'column',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          column: {
            allowPointSelect: selectionState,
            cursor: cursorState,
            events: markEvents,
            dataLabels: {
              inside: false,
            },
            states: {
              select: {
                color: null,
              },
              hover: {
                brightness: 0, // disable color change on-hover
                halo: {
                  attributes: {
                    stroke: 'black',
                    'stroke-width': 1,
                  },
                },
              },
            },
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeGroupedTitles(model, encoding))
    );
  }

  // horizontal stacked bar
  function initStackedBarChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeStackedBarMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'bar',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          series: {
            allowPointSelect: selectionState,
            stickyTracking: false, // prevents tooltip from appearing far away from line segment
          },
          bar: {
            cursor: cursorState,
            events: markEvents,
            stacking: 'normal',
            states: {
              hover: {
                brightness: 0, // disable color change on-hover
              },
              select: {
                color: null,
              },
            },
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeStackedTitles(model, encoding))
    );
  }

  // horizontal 100% stacked bar
  function initStackedBar100Chart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeStackedBarMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'bar',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          series: {
            allowPointSelect: selectionState,
            stickyTracking: false, // prevents tooltip from appearing far away from line segment
          },
          bar: {
            cursor: cursorState,
            events: markEvents,
            stacking: 'percent',
            states: {
              hover: {
                brightness: 0, // disable color change on-hover
              },
              select: {
                color: null,
              },
            },
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeStackedTitles(model, encoding))
    );
  }

  function initStackedColumnChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeStackedBarMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const selectionEvents = ChartEvent.makeStackedBarSelectionEvents();
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'column',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          series: {
            allowPointSelect: selectionState,
            stickyTracking: false, // prevents tooltip from appearing far away from line segment
          },
          column: {
            cursor: cursorState,
            events: markEvents,
            stacking: 'normal',
            minPointLength: 3,
            states: {
              hover: {
                brightness: 0, // disable color change on-hover
              },
              select: {
                color: null,
              },
            },
            point: {
              events: selectionEvents,
            },
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeStackedTitles(model, encoding))
    );
  }

  function initStackedColumn100Chart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeStackedBarMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const selectionEvents = ChartEvent.makeStackedBarSelectionEvents();
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'column',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          series: {
            allowPointSelect: selectionState,
            stickyTracking: false, // prevents tooltip from appearing far away from line segment
          },
          column: {
            cursor: cursorState,
            events: markEvents,
            stacking: 'percent',
            states: {
              hover: {
                brightness: 0, // disable color change on-hover
              },
              select: {
                color: null,
              },
            },
            point: {
              events: selectionEvents,
            },
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeStackedTitles(model, encoding))
    );
  }

  function initLineChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeLineMarkEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'line',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          line: {
            allowPointSelect: selectionState,
            cursor: cursorState,
            events: markEvents,
            marker: {
              enabled: true,
              fillColor: null,
              radius: 0.1, // imitates hidden nodes while allowing proper selected state behavior
              symbol: 'circle',
              states: {
                hover: {
                  lineWidth: 0.5,
                  radius: 6,
                  lineColor: null,
                },
                select: {
                  fillColor: null,
                  lineWidth: 0.5,
                  radius: 6,
                },
              },
            },
          },
          series: {
            stickyTracking: false, // prevents tooltip from appearing far away from line segment
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeStackedTitles(model, encoding))
    );
  }

  function initAreaChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeAreaMarkEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'area',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          area: {
            allowPointSelect: selectionState,
            cursor: cursorState,
            events: markEvents,
            lineWidth: 0.5,
            stacking: 'normal',
            marker: {
              enabled: true,
              fillColor: null,
              lineColor: null,
              radius: 0.1, // imitates hidden nodes while allowing proper selected state behavior
              symbol: 'circle',
              states: {
                hover: {
                  lineWidth: 0.5,
                  lineColor: 'black',
                  radius: 6,
                },
                select: {
                  fillColor: null,
                  lineWidth: 0.5,
                  radius: 6,
                },
              },
            },
            stickyTracking: false, // this and 'trackByArea: true' ensure clicking on entire area segment will activate context menu
            trackByArea: true,
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeStackedTitles(model, encoding))
    );
  }

  function initTotalAreaChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeTotalAreaMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'area',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },
        plotOptions: {
          area: {
            allowPointSelect: selectionState,
            cursor: cursorState,
            events: markEvents,
            lineWidth: 0.5,
            stacking: 'percent',
            marker: {
              enabled: true,
              fillColor: null,
              lineColor: null,
              radius: 0.1, // imitates hidden nodes while allowing proper selected state behavior
              symbol: 'circle',
              states: {
                hover: {
                  lineWidth: 0.5,
                  lineColor: 'black',
                  radius: 6,
                },
                select: {
                  fillColor: null,
                  lineWidth: 0.5,
                  radius: 6,
                },
              },
            },
            stickyTracking: false, // this and 'trackByArea: true' ensure clicking on entire area segment will activate context menu
            trackByArea: true,
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeStackedTitles(model, encoding))
    );
  }

  function initScatterChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeScatterMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'scatter',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          scatter: {
            cursor: cursorState,
            events: markEvents,
            marker: {
              symbol: 'circle',
              states: {
                hover: {
                  lineWidth: 1,
                },
                select: {
                  enabled: true,
                  fillColor: null,
                  lineWidth: 1,
                  radius: 6,
                },
              },
            },
          },
          series: {
            allowPointSelect: selectionState,
            stickyTracking: false, // prevents tooltip from appearing far away from line segment
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeScatterTitles(model, encoding))
    );
  }

  function initBubbleChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.BubbleData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makeBubbleMarkEvents(
      model,
      encoding,
      renderOptions.chartToken,
      renderOptions.handler
    );
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'bubble',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          bubble: {
            cursor: cursorState,
            allowPointSelect: selectionState,
            events: markEvents,
            marker: {
              states: {
                hover: {
                  lineWidth: 1,
                },
                select: {
                  enabled: true,
                  fillColor: null,
                  lineWidth: 1,
                },
              },
            },
          },
        },
      },
      initCartesianTitles(chartFormat, ChartTitle.makeScatterTitles(model, encoding))
    );
  }

  function initPieChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartData: ChartRenderTypes.PointData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    const events = ChartEvent.makeChartEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const markEvents = ChartEvent.makePieMarkEvents(model, encoding, renderOptions.chartToken, renderOptions.handler);
    const cursorState = renderOptions.drilldownEnabled ? 'pointer' : undefined;
    const selectionState = renderOptions.drilldownEnabled;

    return merge(
      {
        chart: {
          type: 'pie',
          events: {
            ...events,
            ...(renderOptions.complete != null ? { load: renderOptions.complete } : {}),
          },
        },

        plotOptions: {
          pie: {
            allowPointSelect: selectionState,
            cursor: cursorState,
            events: markEvents,
            showInLegend: true,
            dataLabels: {
              distance: 10,
            },
            slicedOffset: 0,
          },
          series: {
            states: {
              hover: {
                brightness: 0, // disable color change on-hover
              },
              select: {
                fillColor: null,
              },
            },
          },
        },
      },
      initPolarTitles(chartFormat, ChartTitle.makePieTitles(model, encoding))
    );
  }

  function initCartesianTitles(chartFormat: ChartTypes.ChartFormat, titles: ChartTitles): Highcharts.Options {
    return {
      xAxis: [
        {
          title: {
            text: chartFormat.xAxis.titleEnabled ? chartFormat.xAxis.title || titles.xAxis : null,
          },
        },
      ],

      yAxis: [
        {
          title: {
            text: chartFormat.yAxis.titleEnabled ? chartFormat.yAxis.title || titles.yAxis : null,
          },
        },
      ],

      legend: {
        title: {
          text: chartFormat.legend.titleEnabled ? chartFormat.legend.title || titles.legend : null,
        },
      },
    };
  }

  function initComboTitles(chartFormat: ChartTypes.ChartFormat, titles: ChartTitles): Highcharts.Options {
    return {
      xAxis: [
        {
          title: {
            text: chartFormat.xAxis.titleEnabled ? chartFormat.xAxis.title || titles.xAxis : null,
          },
        },
      ],

      yAxis: [
        {
          title: {
            text: chartFormat.yAxis.titleEnabled ? chartFormat.yAxis.title || titles.yAxis : null,
          },
        },
        {
          title: {
            text: chartFormat.y2Axis.titleEnabled ? chartFormat.y2Axis.title || titles.y2Axis : null,
          },
        },
      ],

      legend: {
        title: {
          text: chartFormat.legend.titleEnabled ? chartFormat.legend.title || titles.legend : null,
        },
      },
    };
  }

  function initPolarTitles(chartFormat: ChartTypes.ChartFormat, titles: ChartTitles): Highcharts.Options {
    return {
      legend: {
        title: {
          text: chartFormat.legend.titleEnabled ? chartFormat.legend.title || titles.legend : null,
        },
      },
    };
  }

  /**
   * Combo Data
   * Chart Types:
   *  Combo
   */

  function initComboData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    return prepareComboData(model, encoding, chartFormat);
  }

  function prepareComboData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    const seriesFields = new Array<ChartRenderTypes.SeriesField>();
    encoding.marks.series.forEach((series, index) => {
      const yEncField = lookupYField(encoding, series.field);
      const yField = model.makeField(yEncField);

      let colorField: FlamingoTypes.ModeField;
      if (series.properties.color && series.properties.color.values.length > 0) {
        colorField = model.makeField(series.properties.color.values[0]);
      } else {
        colorField = model.buildViewNamesField('Measure Names');
      }

      let seriesType: string;
      switch (series.properties.type.value) {
        case MarkType.Bar:
          seriesType = 'column';
          break;
        case MarkType.Line:
          seriesType = 'line';
          break;
        case MarkType.Area:
          seriesType = 'area';
          break;
        default:
          seriesType = 'line';
          break;
      }

      seriesFields.push({
        type: seriesType,
        name: yField.name,
        groupField: colorField,
        yField: yField,
        yAxis: index,
      });
    });

    const xField = model.makeField(encoding.x[0]);
    return ChartData.makeComboData(model, encoding, xField, seriesFields, chartFormat);
  }

  /**
   * Grouped Data
   * Chart Types:
   *  Bar
   */

  function initGroupedData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    return prepareGroupedData(model, encoding, chartFormat);
  }

  function prepareGroupedData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    const yField = model.makeField(encoding.y[0]);
    const groupField = model.makeField(encoding.column[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    } else {
      colorField = model.buildViewNamesField('Measure Names');
    }

    return ChartData.makeLineData(model, encoding, colorField, groupField, yField, chartFormat);
  }

  /**
   * Stacked Data
   * Chart Types:
   *  Line, Stacked Bar, Area, TotalArea
   */

  function initStackedData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    return prepareStackedData(model, encoding, chartFormat);
  }

  export function prepareStackedData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    } else {
      colorField = model.buildViewNamesField('Measure Names');
    }

    return ChartData.makeLineData(model, encoding, colorField, xField, yField, chartFormat);
  }

  /**
   * Other Data
   * Chart Types:
   *  Pie, Donut, Scatter, Bubble
   */

  function initBubbleData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.BubbleData {
    return prepareBubbleData(model, encoding, chartFormat);
  }

  export function prepareBubbleData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.BubbleData {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);
    const sizeField = model.makeField(encoding.marks.series[0].properties.size.values[0]);
    const colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    return ChartData.makeBubbleData(model, encoding, colorField, xField, yField, sizeField, chartFormat);
  }

  function initPieData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.PointData {
    return preparePieData(model, encoding, chartFormat);
  }

  function initDonutData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.PointData {
    return prepareDonutData(model, encoding, chartFormat);
  }

  export function preparePieData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.PointData {
    const colorField = model.makeField(encoding.marks.properties.color.values[0]);
    const angleField = model.makeField(encoding.marks.properties.angle.values[0]);
    return ChartData.makeNamedPointData(model, encoding, colorField, angleField, chartFormat);
  }

  export function prepareDonutData(
    model: RenderDataModel,
    encoding: Encoding,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.PointData {
    const colorField = model.makeField(encoding.marks.properties.color.values[0]);
    const angleField = model.makeField(encoding.marks.properties.angle.values[0]);
    return ChartData.makePairedPointData(model, encoding, colorField, angleField, chartFormat);
  }
}
